import { useQuery } from "react-query";

import {
  CUSTOMER_MANAGEMENT_SERVICE_ADDRESS,
  IS_SSO_BRIDGE
} from "constants/app.constants";

import {
  effectiveCustomer,
  fetchApplications,
  fetchCurrentUser,
  noviApexHost
} from "api/customerManagement";

import { Product, reachableProducts } from "../products";

export const APPLICATIONS_QUERY_KEY = "customerManagementApplications";
export const CURRENT_USER_QUERY_KEY = "customerManagementCurrentUser";

/**
 * Whether the app switcher can exist in this build at all.
 *
 * The suite it switches between only exists behind the Novi SSO bridge, and the
 * service that says which of those apps you may reach is only deployed there.
 * Both are build-time facts, so a build without them never mounts the switcher
 * and never makes the calls.
 *
 * A function rather than a module-level constant so the flags are read when
 * asked rather than captured at import. Vite inlines them either way in a
 * build, and it is what lets a test toggle the env without resetting modules.
 */
export const isAppSwitcherEnabled = (): boolean =>
  IS_SSO_BRIDGE && !!CUSTOMER_MANAGEMENT_SERVICE_ADDRESS;

/**
 * The products to offer, or `null` when the switcher should not be shown.
 *
 * Two calls, because the answer is two halves that belong to different owners:
 * `/applications` is the app-to-permission table (a catalogue, the same one the
 * M&A app fetches), and `/users/current` is who is asking. Crossing them is
 * `reachableProducts`. They are separate queries rather than one so each is
 * cached on its own — the table is the same for everybody.
 *
 * `null` is the answer whenever *either* half is missing, and the cases are
 * deliberately not distinguished: the build has no bridge, a call has not
 * answered yet, or a call failed. All of them mean the same thing on screen —
 * no switcher — and collapsing them is what keeps a failed call from flashing a
 * control that then disappears, or worse, one that offers products the user
 * cannot open. Half an answer is not a smaller answer: without the table there
 * are no permissions to check against, and without the user there is nothing to
 * check.
 *
 * This is the opposite of `reachableProducts`' bias, and the two are answering
 * different questions. Within a *known* table, a product with no row is kept
 * because absence of a row is not evidence. With no table at all there is
 * nothing to be biased about, and a navigation control built on a guess is
 * worse than none.
 *
 * Retries are off for the same reason: an unavailable service is a hidden
 * button, not a spinner, and three more round trips would not change that.
 */
export function useAppSwitcherProducts(): Nullable<Product[]> {
  const enabled = isAppSwitcherEnabled();
  // Neither the licence table nor a user's permissions change during a session,
  // so each is asked once and kept.
  const options = {
    enabled,
    retry: false as const,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  };

  const applications = useQuery(APPLICATIONS_QUERY_KEY, fetchApplications, options);
  const user = useQuery(CURRENT_USER_QUERY_KEY, fetchCurrentUser, options);

  if (!applications.data || !user.data) return null;

  // The other Novi apps are served per customer, so the tiles link to that
  // customer's subdomain rather than to a path under EVA's own.
  return reachableProducts(applications.data, user.data.permissions, {
    customer: effectiveCustomer(user.data),
    apexHost: noviApexHost()
  });
}
